// User types
export interface User {
  avatar?: any
  id: string
  firstName: string
  lastName: string
  email: string
  role: UserRole // ✅ change this
  originalRole?: UserRole
  profilePhotoUrl?: string
  createdAt?: string
  updatedAt?: string
  hasHippaSigned?: boolean
  hasSignedConsent?: boolean // For patient intake consent
}

export enum UserRole {
  ADMIN = 'admin',
  THERAPIST = 'therapist',
  CLIENT = 'client',
  CERTIFICATE_MANAGER = 'certificate_manager',
}

// Authentication types
export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  role: UserRole
}

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
}

// Therapist-specific API response types
export interface TherapistLoginResponse {
  message: string
  access_token: string
  refresh_token: string
  therapist: {
    id: string
    firstName: string
    lastName: string
    email: string
    profilePhotoUrl?: string
    hasSignedHIPAA?: boolean
  }
}

// OTP Verification Response
export interface OtpVerificationResponse {
  message: string
  step: 'OTP_VERIFICATION_REQUIRED' | 'VERIFIED'
  email: string
}

export interface OtpVerificationRequest {
  email: string
  otp: string
}

export interface OtpVerificationSuccessResponse {
  user: User
  token: string
  refreshToken: string
}

// API types
export interface ApiResponse<T = any> {
  data: T
  message: string
  success: boolean
}

export interface ApiError {
  message: string
  status: number
  errors?: Record<string, string[]>
}

// Common types
export interface PaginationParams {
  page: number
  limit: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// Component types
export interface BaseComponentProps {
  className?: string
  children?: React.ReactNode
}

// Navigation types
export interface NavigationItem {
  [x: string]: any
  text: string
  icon: React.ReactElement
  path: string
  roles?: UserRole[]
  badge?: string | number
  disabled?: boolean
}

// Route types
export interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: UserRole | UserRole[]
  redirectTo?: string
}

export interface RouteConfig {
  path: string
  element: React.ComponentType
  requiredRole?: UserRole | UserRole[]
  exact?: boolean
}

// Therapist Scheduling types
export interface TimeRange {
  id: string
  startTime: string
  endTime: string
}

export interface WorkingDay {
  _id?: string // MongoDB _id for API calls
  day: string
  enabled: boolean
  timeRanges: TimeRange[]
}

export interface DateOverride {
  id: string
  date: string
  type: 'unavailable' | 'available'
  timeRanges?: TimeRange[]
  reason?: string
}

// Schedule Exception types for temporary unavailability
export interface ScheduleException {
  _id?: string // MongoDB _id for API calls
  type: 'date_range' | 'partial_day'
  start: string // ISO datetime string
  end: string // ISO datetime string
  reason?: string
}

export interface TherapistAvailability {
  id: string
  therapistId: string
  acceptingAppointments: boolean
  workingDays: WorkingDay[]
  dateOverrides: DateOverride[]
  scheduleExceptions?: ScheduleException[] // Add exceptions array
  updatedAt: string
}

export interface AppointmentSlot {
  id: string
  date: string
  startTime: string
  endTime: string
  status: 'available' | 'booked' | 'unavailable'
  patientId?: string
  patientName?: string
  sessionType?: string
  notes?: string
}

export interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  backgroundColor: string
  borderColor: string
  textColor: string
  extendedProps: {
    status: string
    patientName?: string
    sessionType?: string
    sessionId?: string
    reason?: string
    isException?: boolean
  }
}

// HIPAA Consent types
export interface HippaConsentData {
  userId: string
  agreementTemplateId: string
  agreementVersion: string
  fullName: string
  ipAddress: string
  userAgent: string
  consentTime: string // ISO date
  signatureType: string
  drawnSignature: string // base64 image data URL
  initial_1?: string
  initial_2?: string
  initial_3?: string
  initial_4?: string
  initial_5?: string
  initial_6?: string
  companyName?: string
}

export interface HippaConsentResponse {
  message?: string
  hasSignedHIPAA?: boolean
  [key: string]: any // Allow other response fields
}

// Patient Intake & Consent Packet types
export interface PatientConsentData {
  ipAddress: string
  patientSignature: string // base64 image data URL
  guardianSignature?: string // base64 image data URL (optional, for minors)
  guardianName?: string // Guardian name (optional, for minors)
  initial_1?: string
  initial_2?: string
  initial_3?: string
  initial_4?: string
  initial_5?: string
  initial_6?: string
  initial_7?: string
  initial_8?: string
  initial_9?: string
}

export interface PatientConsentResponse {
  message?: string
  hasSignedConsent?: boolean
  [key: string]: any // Allow other response fields
}

// Family Member / Caregiver Access Consent types
export interface FamilyConsentData {
  patient_id: string
  ipAddress: string
  familySignature: string // base64 image data URL
  patientSignature: string // base64 image data URL
  patientName: string
}

export interface FamilyConsentResponse {
  message?: string
  success?: boolean
  [key: string]: any // Allow other response fields
}

// Re-export onboarding types
export * from './onboarding'
